import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import DocPageHeader from 'components/docs/DocPageHeader';
import { useAuth } from 'hooks/useAuth';
import { BillingInfo } from 'models/Billing';
import { getBilling } from 'services/billingService';
import BillingStatusPanel from 'shiplab-components/BillingStatusPanel';
import { isTrialOrActive } from 'utils/billing';
import { markSetupPopupCompleted } from 'utils/setupPopup';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 45000;

const PlansBillingCompletePage: React.FC = () => {
  const { accessToken, username } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [polling, setPolling] = useState(true);
  const startedAt = useRef(Date.now());

  const refreshOnce = useCallback(async () => {
    if (!accessToken) {
      return null;
    }
    const data = await getBilling(accessToken);
    setBilling(data);
    return data;
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      setPolling(false);
      return;
    }

    let cancelled = false;
    startedAt.current = Date.now();
    setTimedOut(false);
    setPolling(true);
    setError(null);

    const tick = async () => {
      try {
        const data = await refreshOnce();
        if (cancelled || !data) {
          return;
        }
        if (isTrialOrActive(data)) {
          markSetupPopupCompleted(username);
          setPolling(false);
          return;
        }
        if (Date.now() - startedAt.current >= POLL_TIMEOUT_MS) {
          setTimedOut(true);
          setPolling(false);
          return;
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to refresh billing'
          );
          setPolling(false);
        }
      }
    };

    void tick();
    const intervalId = window.setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [accessToken, refreshOnce]);

  const activated = billing ? isTrialOrActive(billing) : false;

  useEffect(() => {
    if (activated) {
      markSetupPopupCompleted(username);
    }
  }, [activated, username]);

  return (
    <div>
      <DocPageHeader title="Plans & Billing" />

      {!sessionId && (
        <Alert variant="warning" className="mb-4">
          Missing checkout session. You can return to{' '}
          <Link to="/pages/plans-billing">Plans & Billing</Link>.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => {
                setError(null);
                setPolling(true);
                setTimedOut(false);
                startedAt.current = Date.now();
                void refreshOnce().catch(err =>
                  setError(
                    err instanceof Error
                      ? err.message
                      : 'Failed to refresh billing'
                  )
                );
              }}
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {polling && !activated && (
        <Card className="border border-200 mb-4">
          <Card.Body className="d-flex align-items-center gap-3">
            <Spinner animation="border" size="sm" />
            <div>
              <h5 className="mb-1">Payment details received — activating your trial.</h5>
              <p className="mb-0 text-700">
                This usually takes a few seconds. Hang tight while we confirm
                your subscription.
              </p>
            </div>
          </Card.Body>
        </Card>
      )}

      {timedOut && !activated && (
        <Alert variant="info" className="mb-4">
          Your payment was received, but activation is still in progress. You
          can refresh this page or check{' '}
          <Link to="/pages/plans-billing">Plans & Billing</Link> in a moment.
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => {
                setTimedOut(false);
                setPolling(true);
                startedAt.current = Date.now();
                void refreshOnce();
              }}
            >
              Refresh status
            </Button>
          </div>
        </Alert>
      )}

      {activated && billing && <BillingStatusPanel billing={billing} />}
    </div>
  );
};

export default PlansBillingCompletePage;
